<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { UiDialog, UiText } from '@shopexperts/shared';
import type { AdminExpertClaim } from '@shopexperts/legacy-api';
import ClaimNoteField from './ClaimNoteField.vue';
import { isClaimNoteOverLimit } from './claimAppearance';

const props = defineProps<{
  claim: AdminExpertClaim | null;
  isLoading?: boolean;
  /** Unsaved drawer note draft, prefilled so typed context isn't lost. */
  initialNote?: string;
}>();

const model = defineModel<boolean>({ default: false });

const emit = defineEmits<{
  confirm: [note?: string];
}>();

const note = ref('');

watch(model, (isOpen) => {
  note.value = isOpen ? (props.initialNote ?? '') : '';
});

const isNoteOverLimit = computed(() => isClaimNoteOverLimit(note.value));

function handleConfirm() {
  if (isNoteOverLimit.value) return;
  const trimmed = note.value.trim();
  emit('confirm', trimmed || undefined);
}
</script>

<template>
  <UiDialog
    v-model="model"
    title="Approve claim"
    :text="claim ? `Give ${claim.claimant_name || claim.email} ownership of ${claim.expert.name || claim.expert.slug}?` : ''"
    primary-action-label="Approve claim"
    secondary-action-label="Cancel"
    :loading="isLoading"
    @primary-action="handleConfirm"
    @secondary-action="model = false"
  >
    <div v-if="claim" class="flex flex-col gap-4">
      <div>
        <UiText as="p" variant="p-small" class="text-gray-600 mb-1.5" text="Approving will:" />
        <ul class="list-disc pl-5 space-y-1">
          <li>
            <UiText as="span" variant="p-small" class="text-gray-600">
              <span>Create an account for <span class="font-medium text-gray-800">{{ claim.email }}</span> — or attach their existing account if one exists</span>
            </UiText>
          </li>
          <li>
            <UiText
              as="span"
              variant="p-small"
              class="text-gray-600"
              text="Make them the owner of this expert's organization"
            />
          </li>
          <li>
            <UiText
              as="span"
              variant="p-small"
              class="text-gray-600"
              text="Auto-reject any other undecided claims for this profile"
            />
          </li>
          <li>
            <UiText
              as="span"
              variant="p-small"
              class="text-gray-600"
              text="Email them a create-password link (new account) or a log-in link (existing account)"
            />
          </li>
        </ul>
      </div>

      <ClaimNoteField
        v-model="note"
        label="Admin note (optional)"
        placeholder="Add a note about this decision..."
        :disabled="isLoading"
      />
    </div>
  </UiDialog>
</template>
